import { useEffect, useState } from 'react';
import { ReactComponent as FileIcon } from 'assets/file-icon.svg';
import { Table, TableBody, TableRow, TableCell, Button } from '@mui/material';
import MuiTextField from 'components/Form/MuiTextField';
import useNotification from 'hooks/useNotification';
import { IFormValues } from 'routes/Main/type';
import { getAssignment, deleteAssignment } from 'apis/video';
import axios from 'axios';
import DeleteConfirmButton from '../CoreNursingSkillVideo/components/DeleteConfirmButton';
import { formatDate } from 'utils/formatting';
import useStudent from 'store/student/useStudent';
import useUser from 'store/user/useUser';

interface Props extends IFormValues {
  disabled?: boolean;
  user_id: number;
  patient_id: number;
  patient_name: string;
  totalSize: number;
  refresh: boolean;
  setTotalSize: (totalSize: number) => void;
}

interface FileInfo {
  saved: boolean;
  path: string;
  file: File | null;
  assignment_num?: number;
  assignment_date?: string;
}

const AssignmentVideoForm = (props: Props) => {
  const {
    disabled,
    user_id,
    patient_id,
    patient_name,
    totalSize,
    setTotalSize,
    refresh,
    setValue,
  } = props;

  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { student_uuid } = useStudent();
  // 1: 학생 / 2,5: 교수
  const { student_grade } = useUser();

  const [files, setFiles] = useState<FileInfo[]>([
    { saved: false, path: '', file: null, assignment_num: 1 },
    { saved: false, path: '', file: null, assignment_num: 2 },
    { saved: false, path: '', file: null, assignment_num: 3 },
  ]);

  const [yesDelet, setYesDelet] = useState(false);
  const [selectedAssignmentNum, setSelectedAssignmentNum] = useState<
    number | null
  >(null);

  const getFieldName = (assignmentNum: number) => {
    return assignmentNum === 1 ? 'file' : `file${assignmentNum}`;
  };

  const calculateTotalSize = (files: FileInfo[]) => {
    return files.reduce((acc, file) => {
      if (file.file && file.file.size) {
        return acc + file.file.size;
      }
      return acc;
    }, 0);
  };

  const deleteFile = () => {
    if (!yesDelet || selectedAssignmentNum === null) return;

    const assignmentNum = selectedAssignmentNum;

    deleteAssignment({ user_id, patient_id, assignment_num: assignmentNum })
      .then(res => {
        const fileIndex = files.findIndex(
          f => f.assignment_num === assignmentNum
        );
        if (fileIndex === -1) return;

        const newFiles = [...files];
        newFiles[fileIndex] = {
          saved: false,
          path: '',
          file: null,
          assignment_num: assignmentNum,
        };

        setFiles(newFiles);

        const newSize = calculateTotalSize(newFiles);
        setTotalSize(newSize);

        setValue(getFieldName(assignmentNum), []);
        setSelectedAssignmentNum(null);
        setYesDelet(false);
      })
      .catch(e => onFail('삭제에 실패하였습니다', e));
  };

  const downloadFile = (assignmentNum: number) => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/file/assignment_download?user_id=${user_id}&patient_id=${patient_id}&assignment_num=${assignmentNum}`,
        {
          responseType: 'blob',
        }
      )
      .then(res => {
        const fileInfo = files.find(f => f.assignment_num === assignmentNum);
        if (!fileInfo?.path) return;

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileInfo.path;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(e => onFail('다운로드에 실패하였습니다', e));
  };

  useEffect(() => {
    const requestUserId =
      student_grade === 2 || student_grade === 5 ? student_uuid : user_id;

    getAssignment({ user_id: requestUserId, patient_id })
      .then(({ data }) => {
        const { file_info } = data;

        let savedFiles = [...files];

        file_info.forEach((file: any) => {
          const index = file.assignment_num - 1;
          savedFiles[index] = {
            saved: true,
            path: file.assignment_name,
            file: { size: file.assignment_size } as File,
            assignment_num: file.assignment_num,
            assignment_date: file.assignment_date,
          };
          setValue(getFieldName(file.assignment_num), [
            { size: file.assignment_size },
          ]);
        });

        while (savedFiles.length < 3) {
          const num = savedFiles.length + 1;
          savedFiles.push({
            saved: false,
            path: '',
            file: null,
            assignment_num: num,
            assignment_date: '',
          });
          setValue(getFieldName(num), []);
        }

        setFiles(savedFiles);

        const newSize = calculateTotalSize(savedFiles);
        setTotalSize(newSize);
      })
      .catch(e => {
        if (e.response.data.status !== 500)
          onFail('과제를 불러오는 데 실패하였습니다', e);
      });
  }, [refresh]);

  useEffect(() => {
    if (!yesDelet) {
      setSelectedAssignmentNum(null);
    } else if (selectedAssignmentNum !== null) {
      deleteFile();
    }
  }, [yesDelet]);

  return (
    <Table sx={{ width: '100%', marginTop: '24px' }} aria-label="simple table">
      <TableBody>
        {files.map(file => (
          <TableRow
            key={file.assignment_num}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell>과제 {file.assignment_num}</TableCell>
            <TableCell>{patient_name}</TableCell>
            <TableCell>
              {file.saved ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <MuiTextField
                    value={file.path}
                    InputProps={{ endAdornment: <FileIcon />, readOnly: true }}
                    onClick={() =>
                      file.assignment_num && downloadFile(file.assignment_num)
                    }
                    sx={{ width: '300px' }}
                  />
                  <span>{formatDate(file.assignment_date || '')}</span>
                </div>
              ) : (
                <MuiTextField
                  required={false}
                  type="file"
                  value={file.path}
                  onChange={e => {
                    const target = e.target as HTMLInputElement;
                    if (!target.files || !file.assignment_num) return;

                    const newFile = target.files[0];

                    if (newFile.size > 512000000) {
                      return onRequired('REQUIRED.VIDIEO.FORMAT');
                    }

                    const newFiles = [...files];
                    const index = file.assignment_num - 1;
                    newFiles[index] = {
                      saved: false,
                      path: target.value,
                      file: newFile,
                      assignment_num: file.assignment_num,
                    };

                    setFiles(newFiles);

                    const newSize = calculateTotalSize(newFiles);
                    setTotalSize(newSize);

                    setValue(getFieldName(file.assignment_num), [newFile]);
                  }}
                  InputProps={{ endAdornment: <FileIcon /> }}
                  sx={{ width: '300px' }}
                />
              )}
            </TableCell>
            <TableCell sx={{ width: '10%' }}>
              {student_grade === 1 ? (
                file.path !== '' ? (
                  <DeleteConfirmButton
                    yesDelet={yesDelet}
                    setYesDelet={setYesDelet}
                    i={file.assignment_num || 0}
                    deleteFile={() => {
                      setSelectedAssignmentNum(file.assignment_num || null);
                      setYesDelet(true);
                    }}
                    title="삭제"
                    middleTitle="정말 삭제하시겠어요?"
                    message="삭제 버튼 선택 시, 해당 내용은 복구되지 않습니다."
                    color="white"
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disabled
                    onClick={() => null}
                  >
                    삭제
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  onClick={() => null}
                >
                  삭제
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AssignmentVideoForm;
